import { useState, useCallback } from "react";
import { Row, Col, Card, CardTitle, CardSubtitle } from "reactstrap";
import { CRow, CCol, CForm, CFormLabel, CFormInput } from "@coreui/react";
import {
  HandleUserNameChange,
  HandleStudentClassChange,
  HandleUserEmailChange,
  CheckIfEmailIsOccupied,
  SaveStudentFocusControlSetting,
  DeleteStudentFocusControlSetting,
} from "../../actions/teacherMutations";
import {
  GetCoursesByStudentEmail,
  GetCoursesWithResultsByStudentId,
} from "../../actions/teacherQueries";
import ButtonSendActivationMail from "../Button/ButtonSendActivationMail";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import FinalExamUnlockerContainer from "./FinalExamUnlockerContainer";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import { FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { checkNameLengthAndCharacters } from "../../Utilities/checkerFunctions";
import ModuleLocker from "./ModuleLocker";
import FocusControl from "../Teacher/FocusControl";
import useFocusControl from "./hooks/useFocusControl";

export default function StudentAdministration(props) {
  const queryClient = useQueryClient();

  const [updatingStudent, setUpdatingStudent] = useState(false);
  const [showCourses, setShowCourses] = useState(false);
  const [emailOccupied, setEmailOccupied] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const mutateStudentName = useMutation({ mutationFn: HandleUserNameChange });
  const mutateStudentClass = useMutation({ mutationFn: HandleStudentClassChange });
  const mutateStudentEmail = useMutation({ mutationFn: HandleUserEmailChange });
  const mutateCheckStudentEmail = useMutation({ mutationFn: CheckIfEmailIsOccupied });

  const studentCourses = GetCoursesByStudentEmail(
    props.studentHasLicense ? props.student.email : null
  );
  const studentCoursesDataWithResult = GetCoursesWithResultsByStudentId(
    props.student.id
  );

  const onRefreshSettings = useCallback(async () => {
    const studentId = String(props.studentId ?? props.student.id);

    await queryClient.refetchQueries({
      queryKey: ["FetchStudentInfoByStudentId", studentId],
    });
    const updatedStudent = queryClient.getQueryData([
      "FetchStudentInfoByStudentId",
      studentId,
    ]);
    return updatedStudent?.focusControlSettings || [];
  }, [props.student.id, props.studentId, queryClient]);

  const fc = useFocusControl({
    settingKey: "studentSetting",
    entityId: props.student.id,
    entityIdKey: "studentId",
    focusControlSettings: props.student.focusControlSettings,
    saveFn: SaveStudentFocusControlSetting,
    deleteFn: DeleteStudentFocusControlSetting,
    onRefreshSettings,
  });

  if (studentCourses.isPending || studentCoursesDataWithResult.isPending) {
    return (
      <div className="inline-pacman-loader">
        <PacmanLoader color="#20a8d8" size={20} />
      </div>
    );
  }

  let values = {
    student: props.student,
    oldEmail: props.student.email,
    newEmail: props.student.email,
    oldName: props.student.name,
    newName: props.student.name,
    class: {
      name: props.student.class.name,
      id: props.student.class.id,
      schoolId: props.student.class.schoolId,
    },
  };

  const handleChangedStudentName = (event) => {
    if (event.target.value !== props.student.name) {
      const nameCheck = checkNameLengthAndCharacters(event.target.value);
      if (nameCheck !== true) {
        toast.warn(nameCheck);
      } else {
        values.oldName = props.student.name;
        let studentData = props.student;
        studentData.name = event.target.value;
        queryClient.setQueryData(["GetStudentInfoByStudentId"], studentData);

        setUpdatingStudent(true);
        mutateStudentName.mutate(
          {
            oldEmail: values.oldEmail,
            newEmail: values.oldEmail,
            newName: event.target.value,
          },
          {
            onSuccess: (data) => {
              if (data && data.status > 199 && data.status < 300) {
                queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
                setUpdatingStudent(false);
                toast.success(<FormattedMessage id="StudentUpdated" />);
              } else {
                toast.warn(data);
              }
            },
            throwOnError: true,
            onError: (data) => {
              studentData.name = values.oldName;
              queryClient.setQueryData(
                ["GetStudentInfoByStudentId"],
                studentData
              );
              setUpdatingStudent(false);
              toast.warn(data.response.data);
            },
          }
        );
      }
    }
  };

  const handleEmailWarningChange = () => {
    if (invalidEmail) setInvalidEmail(false);
    if (emailOccupied) setEmailOccupied(false);
  };

  const checkEmailOnChange = (event) => {
    if (
      !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        event.target.value
      )
    ) {
      setInvalidEmail(true);
    } else {
      setInvalidEmail(false);
      if (event.target.value === values.oldEmail) {
        setEmailOccupied(false);
        return;
      }
      mutateCheckStudentEmail.mutate(
        {
          oldEmail: values.oldEmail,
          newEmail:
            event.target.name === "email" &&
            event.target.value !== props.student.email
              ? event.target.value
              : values.newEmail,
          studentName:
            event.target.name === "name" &&
            event.target.value !== props.student.name
              ? event.target.value
              : values.newName,
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              if (data.data === true) {
                setEmailOccupied(true);
              } else {
                setEmailOccupied(false);
                handleChangedStudentEmail(event);
              }
            } else {
              toast.warn(data);
            }
          },
          throwOnError: true,
          onError: (data) => {
            setUpdatingStudent(false);
            toast.warn(data.response.data);
          },
        }
      );
    }
  };

  const handleChangedStudentEmail = (event) => {
    let studentData = props.student;
    studentData.email = event.target.value;
    queryClient.setQueryData(["GetStudentInfoByStudentId"], studentData);

    setUpdatingStudent(true);
    mutateStudentEmail.mutate(
      {
        oldEmail: values.oldEmail,
        newEmail:
          event.target.name === "email" ? event.target.value : values.oldEmail,
        userName:
          event.target.name === "name" &&
          event.target.value !== props.student.name
            ? event.target.value
            : values.newName,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            setUpdatingStudent(false);
            toast.success(<FormattedMessage id="StudentUpdated" />);
            queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
            queryClient.invalidateQueries({ queryKey: [
              "GetCoursesWithResultsByStudentId",
              props.student.id,
            ] });
          } else {
            toast.warn(data);
          }
        },
        throwOnError: true,
        onError: (data) => {
          let studentData = props.student;
          studentData.email = values.oldEmail;
          queryClient.setQueryData(["GetStudentInfoByStudentId"], studentData);
          setUpdatingStudent(false);
          toast.warn(data.response.data);
        },
      }
    );
  };

  const handleChangedStudentClass = (value) => {
    setUpdatingStudent(true);
    mutateStudentClass.mutate(
      {
        id: props.student.id,
        name: props.student.name,
        class: {
          name: value.value.name,
          id: value.value.id,
          schoolId: props.student.class.schoolId,
        },
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });

            setUpdatingStudent(false);
            toast.success(<FormattedMessage id="StudentUpdated" />);
          } else {
            toast.warn(data);
          }
        },
        throwOnError: true,
        onError: (data) => {
          setUpdatingStudent(false);
          toast.warn(data.response.data);
        },
      }
    );
  };

  if (props.school) {
    if (!props.student.class)
      props.student.class = {
        id: 0,
        name: "",
      };
    props.student.class.schoolId = props.school.id;
  }

  var teacherClasses = [];
  props.teacherInfo.programmes.forEach((program) => {
    program.classes.forEach((cl) => {
      teacherClasses.push(cl);
    });
  });

  const toggleCoursesDropdown = () => {
    setShowCourses((prevstate) => !prevstate);
  };

  const searchList = () => {
    return studentCourses.data.map((course, i) => (
      <Row key={i}>
        <Col>{course.name}</Col>
      </Row>
    ));
  };

  const classSelectOptions = [];
  teacherClasses.forEach((cl) => {
    let cls = { value: cl, label: cl.name };
    classSelectOptions.push(cls);
  });

  const currentClassOption = { value: "", label: "" };
  if (props.student.class.name != null) {
    currentClassOption.value = props.student.class.name;
    currentClassOption.label = props.student.class.name;
  }

  return (
    <div className="animated fadeIn">
      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="StudentInformation" />
            {updatingStudent ? (
              <div style={{ float: "right" }}>
                {" "}
                <div style={{ marginTop: "-11%", marginBottom: "22%" }}>
                  <PacmanLoader color={"#20a8d8"} size={15} loading={true} />
                </div>
                <FormattedMessage id="Updating" />
              </div>
            ) : (
              ""
            )}
            <hr />
          </CardTitle>
          <CardSubtitle>
            <Row>
              <Col lg={8}></Col>
              <Col lg={4}>
                <ButtonSendActivationMail
                  user={props.student}
                  printButtonText={true}
                />
              </Col>
            </Row>
            <CRow>
              <CCol sm="6">
                <CForm action="" method="post">
                  <CFormLabel htmlFor="nf-name">
                    <FormattedMessage id="Name" />:
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="name"
                    name="name"
                    onBlur={handleChangedStudentName}
                    defaultValue={props.student.name}
                    placeholder={props.student.name}
                    autoComplete="false"
                  />
                  <CFormLabel htmlFor="email">
                    <FormattedMessage id="Email" />:
                  </CFormLabel>
                  {emailOccupied ? (
                    <span className="ms-2 text-danger">
                      <FontAwesomeIcon icon="exclamation" className="me-1" />
                      <FormattedMessage id="EmailOccupied" />
                    </span>
                  ) : (
                    ""
                  )}
                  {invalidEmail ? (
                    <span className="ms-2 text-danger">
                      <FontAwesomeIcon icon="exclamation" className="me-1" />
                      <FormattedMessage id="WrongEmailFormat" />
                    </span>
                  ) : (
                    ""
                  )}
                  <CFormInput
                    type="email"
                    id="email"
                    name="email"
                    onBlur={checkEmailOnChange}
                    onChange={handleEmailWarningChange}
                    placeholder={props.student.email}
                    defaultValue={props.student.email}
                    autoComplete="false"
                  />
                  <CFormLabel htmlFor="classAddTeacher">
                    <FormattedMessage id="Class" />:
                  </CFormLabel>
                  <Select
                    disabled={!props.fullAccessTeacher}
                    name="class"
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                    onChange={handleChangedStudentClass}
                    options={classSelectOptions}
                    defaultValue={currentClassOption}
                  />
                </CForm>
              </CCol>
            </CRow>
          </CardSubtitle>
        </Card>
      </Col>

      {props.studentHasLicense && (
        <>
          <FocusControl
            targetSettings={fc.targetSettings}
            onDelete={fc.handleDeleteFocusControl}
            deletingTarget={fc.deletingTarget}
            inheritedSourceId="InheritedFromClassOrProgram"
            onSaveTarget={fc.handleSaveFocusTarget}
            savingTarget={fc.savingTarget}
          />

          <ModuleLocker
            courses={studentCoursesDataWithResult.data}
            level={"Student"}
            currentLevelId={props.student.id}
            students={[props.student.id]}
          />
        </>
      )}

      <FinalExamUnlockerContainer
        programId={props.student.class.programId}
        courses={studentCourses.data}
        searchList={searchList}
        placeHolder={props.placeHolder}
        toggleCoursesDropdown={toggleCoursesDropdown}
        showCourses={showCourses}
        studentCoursesWithStudentModule={studentCoursesDataWithResult}
        studentEmail={props.student.email}
        level="Student"
        studentHasNoLicense={!props.studentHasLicense}
        student={props.student}
        classId={props.student.class.id}
      />
    </div>
  );
}
